import { DateWindowInput } from '@/components/_SiteWide/DateWindowInput'
import { formatTimeWindow } from '@/components/_SiteWide/OrderDisplay/OrderTimeFormats'
import { FileDoneOutlined, LoadingOutlined, TrophyFilled, WarningFilled } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { useOrders } from '@api/useOrders'
import { useOrderDispatchRowPinning } from '@components/OrderDispatch/OrderDispatch'
import { BBDTag, DeltaTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useLockedOrders } from '@hooks/useLockedOrders'
import api from '@utils/api'
import { ReadableDifference } from '@utils/general'
import { Card, Col, Modal, Row, Spin, notification } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import moment, { type Moment } from 'moment'
import { type Key, useEffect, useMemo, useState } from 'react'
import type {
  CarrierAssignFooterProps,
  CarrierCardData,
  DataGridCardsProps,
  ThirdPartyDispatchGridProps,
  ThirdPartyDispatchModalProps,
} from './types'
import { generateCarrierWindowDate, warningMessages } from './util'

export function ThirdPartyDispatchModal({
  order,
  visible,
  dismissModal,
  currentCarrier,
}: ThirdPartyDispatchModalProps) {
  const { rowPinningApiRef } = useOrderDispatchRowPinning()

  const { lockedOrders } = useLockedOrders()
  const optimizationState = useMemo(
    () => lockedOrders?.find((o) => o.order_id === order?.id)?.state || '',
    [lockedOrders, order]
  )

  const [selectedCarrier, setSelectedCarrier] = useState<string | undefined>()
  const [deliveryDate, setDeliveryDate] = useState<{ start: Moment; end: Moment } | undefined>()
  const [buttonLoading, setButtonLoading] = useState(false)
  const { isError, isWarning } = useMemo(() => {
    const result = {
      isError: false,
      isWarning: false,
    }
    if (!order || !deliveryDate) return result

    const retainTime = moment(order.dispatch_window_start)
    const runoutTime = moment(order.dispatch_window_end)
    if (runoutTime.isBefore(moment())) return result

    const invalidStart = deliveryDate.start.isBefore(retainTime)
    const invalidEnd = deliveryDate.end.isAfter(runoutTime)

    result.isError = invalidStart && invalidEnd
    result.isWarning = !result.isError && (invalidStart || invalidEnd)

    return result
  }, [order, deliveryDate])

  // sets the default delivery date and carrier when the modal opens
  useEffect(() => {
    if (!order) {
      setDeliveryDate(undefined)
      setSelectedCarrier(undefined)
      return
    }

    setSelectedCarrier(currentCarrier || order.carrier || order.manager || undefined)
    setDeliveryDate({
      start: generateCarrierWindowDate(order.carrier_window_start, order.default_carrier_window_start),
      end: generateCarrierWindowDate(order.carrier_window_end, order.default_carrier_window_end),
    })
  }, [order, currentCarrier])

  const saveAssignment = () => {
    if (!deliveryDate || !selectedCarrier || !order?.id) {
      return
    }
    setButtonLoading(true)
    const formattedStartDate = deliveryDate.start.toISOString()
    const formattedEndDate = deliveryDate.end.toISOString()
    api
      .assignToCarrier(order.id, selectedCarrier, formattedStartDate, formattedEndDate)
      .then(() => {
        notification.success({
          message: 'Order Assigned To Carrier',
          description: `Your order was successfully assigned to ${`${selectedCarrier} (Order No. ${order.number})`}`,
        })

        rowPinningApiRef?.current?.unpinRow(order.number)
        dismissModal(order, true, selectedCarrier)
        setButtonLoading(false)
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }

  return (
    <Modal
      open={visible}
      onCancel={() => dismissModal(order, false)}
      title='Dispatch to Third Party'
      destroyOnClose
      footer={
        <Horizontal verticalCenter justifyContent='end'>
          {optimizationState === 'pending' ? (
            <BBDTag
              theme1
              className='text-truncate mr-3 p-1'
              style={{
                border: '1px solid var(--theme-color-1)',
              }}
            >
              <FileDoneOutlined className='mr-1' />
              Pending
            </BBDTag>
          ) : (
            optimizationState && (
              <BBDTag
                theme2
                className='text-truncate mr-3 p-1'
                style={{
                  border: '1px solid var(--theme-color-2)',
                }}
              >
                <LoadingOutlined rotate={90} className='mr-1' />
                Optimizing
              </BBDTag>
            )
          )}

          <GraviButton
            key='back'
            buttonText='Dismiss'
            appearance='outline'
            className='mr-3'
            onClick={() => dismissModal(order, false)}
            data-cy='TPD-dismiss-btn'
          />

          <GraviButton
            loading={buttonLoading}
            disabled={!selectedCarrier || !deliveryDate || !!optimizationState}
            key='save'
            onClick={saveAssignment}
            buttonText='Assign To Carrier'
            theme2
          />
        </Horizontal>
      }
      width='80vw'
      style={{ minWidth: 1200, width: '80vw' }}
      className=' tpd'
    >
      <ThirdPartyDispatchGrid
        order={order}
        setSelectedCarrier={setSelectedCarrier}
        selectedCarrier={selectedCarrier}
        deliveryDate={deliveryDate}
        setDeliveryDate={setDeliveryDate}
        isError={isError}
        isWarning={isWarning}
      />
    </Modal>
  )
}

function ThirdPartyDispatchGrid({
  order,
  setSelectedCarrier,
  selectedCarrier,
  deliveryDate,
  setDeliveryDate,
  isError,
  isWarning,
}: ThirdPartyDispatchGridProps) {
  const { getCarrierOptionsQuery } = useOrders()
  const { data } = getCarrierOptionsQuery(order?.id)
  const { formatCurrency } = useFormatter()
  const formattedUnit = formatCurrency(null, true)

  return (
    <>
      {data?.length > 0 && !!order && !!deliveryDate ? (
        <>
          <DataGridCards
            setSelectedCarrier={setSelectedCarrier}
            selectedCarrier={selectedCarrier}
            data={data}
            formattedUnit={formattedUnit}
          />
          <CarrierAssignFooter
            isError={isError}
            isWarning={isWarning}
            deliveryDate={deliveryDate}
            setDeliveryDate={setDeliveryDate}
            order={order}
          />
        </>
      ) : (
        <div className='flex justify-center items-center' style={{ height: 300 }}>
          <Spin size='large' />
        </div>
      )}
    </>
  )
}

function CarrierAssignFooter({ order, deliveryDate, setDeliveryDate, isError, isWarning }: CarrierAssignFooterProps) {
  const getWarningMessage = () => {
    if (!isError && moment(deliveryDate.start).isBefore(order.dispatch_window_start)) {
      return warningMessages.retain
    }
    if (!isError && moment(deliveryDate.end).isAfter(order.dispatch_window_end)) {
      return warningMessages.runout
    }
  }

  const disabledDate = (current: Dayjs) => current.isBefore(dayjs().startOf('day'))

  return (
    <Horizontal className='p-2' gap={16} bg='elevated' border='all'>
      <Vertical style={{ overflow: 'visible' }}>
        <Vertical verticalCenter>
          <Texto style={{ flex: 1 }} category='p2' weight={600}>
            Delivery Window
          </Texto>

          <Texto style={{ whiteSpace: 'nowrap' }} category='p2'>
            {formatTimeWindow(
              order.dispatch_window_start,
              order.dispatch_window_end,
              order.type?.toLowerCase() !== 'backhaul',
              false,
              order.dispatch_window_start_local
            )}
          </Texto>
        </Vertical>
        <Horizontal gap={8} verticalCenter>
          <Texto category='p2' weight={600}>
            Time Until Runout:
          </Texto>

          <Texto category='p2' align='right'>
            {ReadableDifference(order.dispatch_window_end, undefined, true, order.type?.toLowerCase() === 'backhaul')}
          </Texto>
        </Horizontal>
      </Vertical>
      <Horizontal>
        {isWarning && (
          <div className='flex items-center justify-center h-100'>
            <div className='vertical-flex-center h-100 pr-3'>
              <WarningFilled style={{ color: 'var(--theme-warning)', fontSize: '2em' }} />
            </div>

            <div className='vertical-flex-center'>
              <Texto appearance='warning' weight='bold' category='h6' className='mr-2'>
                {getWarningMessage()}
              </Texto>
            </div>
          </div>
        )}
        {isError && (
          <div className='flex items-center justify-center h-100'>
            <div className='vertical-flex-center h-100 pr-3'>
              <WarningFilled style={{ color: 'var(--theme-error)', fontSize: '2em' }} />
            </div>

            <div className='vertical-flex-center pr-3'>
              <Texto appearance='error' weight='bold' category='h6'>
                {warningMessages.outside}
              </Texto>
            </div>
          </div>
        )}
      </Horizontal>
      <Horizontal style={{ overflow: 'visible' }}>
        <div className='vertical-flex-center items-end h-100'>
          <DateWindowInput
            disabledDate={disabledDate}
            showTime={{ format: 'HH:mm' }}
            onChange={(d) =>
              setDeliveryDate({ start: moment(d?.[0]?.toISOString()), end: moment(d?.[1]?.toISOString()) })
            }
            value={[dayjs(deliveryDate.start.toISOString()), dayjs(deliveryDate.end.toISOString())]}
          />
        </div>
      </Horizontal>
    </Horizontal>
  )
}

function DataGridCards({ data, setSelectedCarrier, selectedCarrier, formattedUnit }: DataGridCardsProps) {
  data[0].best = true

  if (data && data.length > 0) {
    return (
      <Row className='carrier-grid flex' title='Third Party Price Comparison'>
        <Card.Grid
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'center',
            overflow: 'auto',
          }}
        >
          {data.map((card: CarrierCardData, index: Key) => {
            const isCurrent =
              card.carrier && selectedCarrier && card.carrier.toLowerCase() === selectedCarrier.toLowerCase()
                ? 'current-carrier'
                : ''
            const isBestClass = card.best ? 'card-is-best' : ''
            const cardClasses = `${isCurrent} ${isBestClass}`
            return (
              <Card key={`${card.carrier}-${index}`} className={cardClasses} style={{ minWidth: 450 }}>
                <Vertical justifyContent={'space-between'}>
                  <Horizontal className='mb-4 py-2'>
                    <Col span={18}>
                      <Horizontal gap={8} className='card-label text-left'>
                        Carrier ({card.assigned_loads} Assigned Loads){' '}
                        {index === 0 && <BBDTag icon={<TrophyFilled />}>Best Option</BBDTag>}
                      </Horizontal>
                      <Horizontal verticalCenter gap={8}>
                        <Texto category='h5' weight='bold' className='card-carrier text-ellipsis'>
                          {card.carrier}
                        </Texto>
                      </Horizontal>
                    </Col>
                    <Col span={6}>
                      <Texto className='card-label text-right'>Cost</Texto>

                      <Texto category='h4' className='card-cost text-right'>
                        {formattedUnit}
                        {card.cost.toFixed(2)}
                      </Texto>
                    </Col>
                  </Horizontal>
                  <Horizontal verticalCenter>
                    <Col span={7} className='vertical-flex items-start'>
                      <Texto className='card-label text-left'>Delta To Min</Texto>
                      <DeltaTag
                        style={{ float: 'left' }}
                        threshold={0}
                        precision={3}
                        textIfEqual='Lowest Price'
                        prefix={formattedUnit}
                        value={card.delta_to_min || 0}
                      />
                    </Col>
                    <Col span={8} className='vertical-flex items-start'>
                      <Texto className='card-label text-left'>Delta To Internal</Texto>
                      <DeltaTag
                        style={{ float: 'left' }}
                        prefix={formattedUnit}
                        precision={3}
                        threshold={0}
                        textIfEqual='Is Internal'
                        value={card.delta_to_default || 0}
                      />
                    </Col>
                    <Col span={9} align='right' className='vertical-flex-start'>
                      <GraviButton
                        size='small'
                        theme2
                        appearance={isCurrent ? 'solid' : 'outlined'}
                        onClick={() => setSelectedCarrier(card.carrier)}
                      >
                        {`${isCurrent ? 'Carrier Selected' : 'Select Carrier'}`}
                      </GraviButton>
                    </Col>
                  </Horizontal>
                </Vertical>
              </Card>
            )
          })}
        </Card.Grid>
      </Row>
    )
  }
  return <></>
}
